'use es6';

import { SCHEDULE_FREQUENCY, DAYS, MONTH_DAYS, LAST_DAY_OF_MONTH } from '../../../lib/schedulingLogic';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { InputStaticLabel } from 'foundations-components/transitional/components/InputStaticLabel';
import { Select } from 'foundations-components/transitional/components/Select';
import { TimePicker } from 'foundations-components/transitional/components/TimePicker';
import { getEventValue } from '../../../utils/jsEventUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EmailSchedulePicker = ({
  onChange,
  scheduleFrequency,
  setScheduleOnDays
}) => {
  const schedulePickerOptions = [{
    value: SCHEDULE_FREQUENCY.DAILY,
    text: I18n.text('reporting-action-components.actions.emailReport.dialog.form.emailFormSection.emailScheduleOptionDaily')
  }, {
    value: SCHEDULE_FREQUENCY.WEEKLY,
    text: I18n.text('reporting-action-components.actions.emailReport.dialog.form.emailFormSection.emailScheduleOptionWeekly')
  }, {
    value: SCHEDULE_FREQUENCY.MONTHLY,
    text: I18n.text('reporting-action-components.actions.emailReport.dialog.form.emailFormSection.emailScheduleOptionMonthly')
  }];
  const handleChange = e => {
    const newScheduleFrequency = getEventValue(e);
    setScheduleOnDays([]); // Reset selected days to an empty array if a user switches the scheduling frequency.
    onChange(newScheduleFrequency);
  };
  return /*#__PURE__*/_jsx(FormControl, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-action-components.actions.emailReport.dialog.form.emailFormSection.emailScheduleFrequencyLabel"
    }),
    children: /*#__PURE__*/_jsx(Select, {
      marginInlineEnd: "200",
      options: schedulePickerOptions,
      onChange: e => handleChange(e),
      value: scheduleFrequency
    })
  });
};
const EmailDayPicker = ({
  onChange,
  scheduleOnDays,
  options,
  deliveryLabel
}) => {
  return /*#__PURE__*/_jsx(FormControl, {
    label: deliveryLabel,
    required: true,
    children: /*#__PURE__*/_jsx(Select, {
      marginInlineEnd: "200",
      onChange: e => {
        const value = getEventValue(e);
        value.sort((a, b) => a - b);
        onChange(value);
      },
      value: scheduleOnDays,
      options: options,
      multi: true,
      anchorType: 'button',
      placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "reporting-action-components.actions.emailReport.dialog.form.emailFormSection.dayPickerPlaceholder"
      })
    })
  });
};
const EmailTimePicker = ({
  scheduleAtHour,
  onChange
}) => {
  const getTimezone = () => I18n.moment.portalTz().format('z');
  return /*#__PURE__*/_jsx(FormControl, {
    label: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-action-components.actions.emailReport.dialog.form.emailFormSection.scheduleTimeLabel"
    }),
    help: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-action-components.actions.emailReport.dialog.form.emailFormSection.emailScheduleWarningText"
    }),
    required: true,
    children: /*#__PURE__*/_jsx(InputStaticLabel, {
      text: getTimezone(),
      position: "end",
      children: /*#__PURE__*/_jsx(TimePicker, {
        clearable: false,
        interval: 60,
        onChange: e => {
          const scheduledAtHour = getEventValue(e) / 60;
          onChange(scheduledAtHour);
        },
        value: scheduleAtHour * 60
      })
    })
  });
};
const EmailScheduling = ({
  scheduleFrequency,
  setScheduleFrequency,
  scheduleOnDays,
  setScheduleOnDays,
  scheduleAtHour,
  setScheduleAtHour
}) => {
  const scheduleOnDayOptions = [{
    value: DAYS.MONDAY,
    text: I18n.text('reporting-action-components.actions.emailReport.dialog.form.emailFormSection.weekDays.monday')
  }, {
    value: DAYS.TUESDAY,
    text: I18n.text('reporting-action-components.actions.emailReport.dialog.form.emailFormSection.weekDays.tuesday')
  }, {
    value: DAYS.WEDNESDAY,
    text: I18n.text('reporting-action-components.actions.emailReport.dialog.form.emailFormSection.weekDays.wednesday')
  }, {
    value: DAYS.THURSDAY,
    text: I18n.text('reporting-action-components.actions.emailReport.dialog.form.emailFormSection.weekDays.thursday')
  }, {
    value: DAYS.FRIDAY,
    text: I18n.text('reporting-action-components.actions.emailReport.dialog.form.emailFormSection.weekDays.friday')
  }, {
    value: DAYS.SATURDAY,
    text: I18n.text('reporting-action-components.actions.emailReport.dialog.form.emailFormSection.weekDays.saturday')
  }, {
    value: DAYS.SUNDAY,
    text: I18n.text('reporting-action-components.actions.emailReport.dialog.form.emailFormSection.weekDays.sunday')
  }];
  const getScheduleOnMonthlyAllDayOptions = () => {
    const monthDays = Object.values(MONTH_DAYS).map(value => {
      return {
        text: `${value}`,
        value
      };
    });
    monthDays.push({
      text: I18n.text('reporting-action-components.actions.emailReport.dialog.form.emailFormSection.monthDays.lastDayOfMonth'),
      value: LAST_DAY_OF_MONTH
    });
    return monthDays;
  };
  const getDayPickerOptions = () => {
    if (scheduleFrequency === SCHEDULE_FREQUENCY.WEEKLY) {
      return scheduleOnDayOptions;
    }
    return getScheduleOnMonthlyAllDayOptions();
  };
  const getDeliveryLabel = () => scheduleFrequency === SCHEDULE_FREQUENCY.WEEKLY ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "reporting-action-components.actions.emailReport.dialog.form.emailFormSection.dayOfWeek"
  }) : /*#__PURE__*/_jsx(FormattedMessage, {
    message: "reporting-action-components.actions.emailReport.dialog.form.emailFormSection.dayOfMonth"
  });
  const shouldRenderDayPicker = scheduleFrequency === SCHEDULE_FREQUENCY.WEEKLY || scheduleFrequency === SCHEDULE_FREQUENCY.MONTHLY;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(EmailSchedulePicker, {
      onChange: setScheduleFrequency,
      scheduleFrequency: scheduleFrequency,
      setScheduleOnDays: setScheduleOnDays,
      scheduleOnDayOptions: scheduleOnDayOptions
    }), shouldRenderDayPicker && /*#__PURE__*/_jsx(EmailDayPicker, {
      onChange: setScheduleOnDays,
      scheduleOnDays: scheduleOnDays,
      scheduleFrequency: scheduleFrequency,
      options: getDayPickerOptions(),
      deliveryLabel: getDeliveryLabel()
    }), /*#__PURE__*/_jsx(EmailTimePicker, {
      onChange: setScheduleAtHour,
      scheduleAtHour: scheduleAtHour
    })]
  });
};
export default EmailScheduling;